/**
 * Sensors query hooks.
 *
 * Every window-scoped hook takes `days` so the sensors page's analysis window and
 * the global scope both flow into the same cache key — two pages asking for the
 * same window share one fetch, and changing the window never shows stale rows.
 */
import { useQuery } from "@tanstack/react-query";

import {
  type ClusterMode,
  fetchHealthMatrix,
  fetchLowBattery,
  fetchMaintenance,
  fetchMissingData,
  fetchQualityStats,
  fetchSensorDetail,
  fetchSensorGeoHealth,
  fetchSensorHealthSummary,
  fetchSensorRegistry,
} from "@/features/sensors/api";
import { thresholds } from "@/lib/thresholds";

const MINUTE = 60_000;
const STALE = 5 * MINUTE;

type Loc = string | null;

export const useSensorHealthSummary = (days: number, locality: Loc = null) =>
  useQuery({
    queryKey: ["sensors", "health-summary", days, locality],
    queryFn: ({ signal }) => fetchSensorHealthSummary(days, locality, signal),
    staleTime: STALE,
  });

export const useSensorGeoHealth = (days: number, locality: Loc = null) =>
  useQuery({
    queryKey: ["sensors", "geo-health", days, locality],
    queryFn: ({ signal }) => fetchSensorGeoHealth(days, locality, signal),
    staleTime: STALE,
  });

export const useSensorRegistry = (days: number, locality: Loc = null) =>
  useQuery({
    queryKey: ["sensors", "registry", days, locality],
    queryFn: ({ signal }) => fetchSensorRegistry(days, locality, signal),
    staleTime: 10 * MINUTE,
  });

export const useLowBattery = (locality: Loc = null, threshold?: number) => {
  const limit = threshold ?? thresholds().battery_low;
  return useQuery({
    queryKey: ["sensors", "low-battery", limit, locality],
    queryFn: ({ signal }) => fetchLowBattery(limit, locality, signal),
    staleTime: STALE,
  });
};

export const useMissingData = (locality: Loc = null, hours?: number) => {
  const window = hours ?? thresholds().active_hours;
  return useQuery({
    queryKey: ["sensors", "missing-data", window, locality],
    queryFn: ({ signal }) => fetchMissingData(window, locality, signal),
    staleTime: STALE,
  });
};

export const useMaintenance = (locality: Loc = null) =>
  useQuery({
    queryKey: ["sensors", "maintenance", locality],
    queryFn: ({ signal }) => fetchMaintenance(locality, signal),
    staleTime: STALE,
  });

export const useHealthMatrix = (
  days: number,
  locality: Loc = null,
  sort: "locality" | "health" = "locality",
  cluster: ClusterMode = "off",
) =>
  useQuery({
    queryKey: ["sensors", "health-matrix", days, locality, sort, cluster],
    queryFn: ({ signal }) => fetchHealthMatrix(days, locality, sort, cluster, signal),
    staleTime: STALE,
  });

export const useQualityStats = (days: number, locality: Loc = null) =>
  useQuery({
    queryKey: ["sensors", "quality", days, locality],
    queryFn: ({ signal }) => fetchQualityStats(days, locality, signal),
    staleTime: STALE,
  });

export const useSensorDetail = (id: string, days?: number) => {
  const window = days ?? thresholds().default_analysis_days;
  return useQuery({
    queryKey: ["sensors", "detail", id, window],
    queryFn: ({ signal }) => fetchSensorDetail(id, window, signal),
    staleTime: STALE,
    enabled: !!id,
  });
};
